
import { useSelector, useDispatch } from "react-redux";
import { createSelector } from "reselect";
import { TLoadable, isLoaded, createLoaded, isUnloaded, createUnloaded, isLoadErrored } from "../../../../sg-core";
import { selectHavainnotState } from "../selectors";
import IObservation, { IObservationCounters } from "../../interfaces/IObservation";
import { createRequestFetchObservationListAction, createRequestFetchObservationPageAction, createUpdateObservationFilters, createSetObservationPaginationPageAction } from "../actions/observationListActions";
import { useMemo, useCallback, useEffect } from "react";
import { IObservationFilters } from "../reducers/observationListReducer";
import { useUsersAppRoles } from "../../auth/store/authSelectors";


// SELECTORS
const selectObservationListState = createSelector(
    selectHavainnotState,
    (workPermit) => workPermit.observationList
);

const selectObservationByIdState = createSelector(
    selectObservationListState,
    (state) => state.observations,
);

const selectObservationIdList = createSelector(
    selectObservationListState,
    (state) => state.observationList,
);

const selectObservationCounters = createSelector(
    selectObservationListState,
    (state) => state.observationCounters,
);

export const selectObservationPagination = createSelector(
    selectObservationListState,
    (state) => state.pagination,
);

const selectObservationPages = createSelector(
    selectObservationListState,
    (state) => state.pages,
);

export const selectObservationFilters = createSelector(
    selectObservationListState,
    (state) => state.filters,
);

const selectObservationList = createSelector(
    selectObservationIdList,
    selectObservationByIdState,
    (observationList, observations): TLoadable<IObservation[]> => {
        if (isLoaded(observationList)) {
            return createLoaded(observationList.data.map((id) => observations[id]));
        }
        return observationList as TLoadable<IObservation[]>
    },
);

// HOOKS 

export const useObservationListPage = (): TLoadable<IObservation[]> => {
    const pagination = useSelector(selectObservationPagination);
    const pages = useSelector(selectObservationPages);
    const page = pages[pagination.page];
    const observations = useSelector(selectObservationByIdState);
    const { filters } = useObservationFilters();
    const dispatch = useDispatch();

    const roleString = filters.roles.join("");


    return useMemo(() => {

        if (!page || isUnloaded(page)) {

            dispatch(createRequestFetchObservationPageAction());
            return createUnloaded();
        } else if (roleString) {

        }

        if (isLoaded(page)) {
            const data = page.data.map((id) => observations[id]);
            return createLoaded(data);
        }
        if (isLoadErrored(page)) {
            return page;
        }
        return createUnloaded();
    }, [page, observations, dispatch, roleString]);
};

export const useObservationList = (filters?: IObservationFilters): TLoadable<IObservation[]> => {
    const observations = useSelector(selectObservationList);
    const dispatch = useDispatch();


    if (isUnloaded(observations)) {
        dispatch(createRequestFetchObservationListAction(filters || { roles: [] }));
    }

    return observations;
}

export const useObservationCounts = (): TLoadable<IObservationCounters> => {
    const counters = useSelector(selectObservationCounters)
    return counters
};

export const useObservationFilters = () => {
    const userRoles = useUsersAppRoles();
    const filters = useSelector(selectObservationFilters);
    const dispatch = useDispatch();

    useEffect(() => {
        if (isLoaded(userRoles) && userRoles.data.join("") !== filters.roles.join("")) {
            dispatch(createUpdateObservationFilters({ roles: userRoles.data }));
        }
    }, [dispatch, userRoles]);

    const updateFilters = useCallback((updated: Partial<IObservationFilters>) => {
        dispatch(createUpdateObservationFilters(updated));
    }, [dispatch]);

    return useMemo(() => ({
        filters,
        update: updateFilters,
    }), [filters, updateFilters]);
};

export const useObservationPagination = () => {
    const pagination = useSelector(selectObservationPagination);
    const dispatch = useDispatch();

    const gotoPage = useCallback((page: number) => {
        dispatch(createSetObservationPaginationPageAction(page));
    }, [dispatch]);

    const switchPage = useCallback((amount: number) => {
        gotoPage(pagination.page + amount);
    }, [pagination.page, gotoPage]);

    const nextPage = useCallback(() => switchPage(1), [switchPage]);
    const prevPage = useCallback(() => switchPage(-1), [switchPage]);

    return useMemo(() => ({
        pagination,
        gotoPage,
        nextPage,
        prevPage,
    }), [pagination, gotoPage, nextPage, prevPage]);
};
